import React from 'react';
import {
    Paper,
    Box,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    LinearProgress,
} from '@mui/material';
import {
    Print,
    ElectricBolt,
    CheckCircle,
    HourglassTop,
    Room,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    formatCurrency,
    formatKwh,
    formatWatt,
    formatShortDate,
    formatBillingPeriod,
    calculateBillingCycleProgress,
    DEFAULT_VAT_RATE,
} from '../../utils/powerCostUtils';
import { getMonthName } from '../../utils/monthlyDataUtils';

function DigitalBillReceipt({ record, selectedZone, selectedYear }) {
    const { t } = useTranslation();

    if (!record) return null;

    const cycleProgress = calculateBillingCycleProgress(
        record.periodStart,
        record.periodEnd,
        record.actualCalculationEnd
    );

    const handlePrint = () => {
        window.print();
    };

    const isPartial = Boolean(record.isPartial);
    const monthName = getMonthName(record.month, t);

    return (
        <Paper
            elevation={3}
            id="printable-digital-bill-receipt"
            sx={{
                p: { xs: 2.5, sm: 4 },
                mb: 3,
                borderRadius: 2.5,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden',
                '@media print': {
                    boxShadow: 'none',
                    border: '1px solid #ddd',
                    p: 3,
                    m: 0,
                },
            }}
        >
            {/* Top Decorative Header Strip */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 6,
                    background: 'linear-gradient(90deg, #10b981 0%, #3b82f6 50%, #8b5cf6 100%)',
                }}
            />

            {/* Bill Header */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 3,
                    pt: 1,
                }}
            >
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <ElectricBolt sx={{ color: '#10b981', fontSize: 28 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
                            {t('common.digitalBillReceipt', 'Digital Electricity Bill Receipt')}
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('common.officialReceiptNote', 'Residential progressive tiered tariff structure')}
                    </Typography>
                </Box>

                {/* Print Button (hidden during print) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, '@media print': { display: 'none' } }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Print />}
                        onClick={handlePrint}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        {t('common.printReceipt', 'Print Receipt')}
                    </Button>
                </Box>
            </Box>

            {/* Meta Information Cards (Period, Zone, Status) */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Billing Period */}
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, height: '100%' }}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                            {t('common.billingPeriod', 'Billing Period')}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {formatBillingPeriod(record.periodStart, record.periodEnd)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {monthName} {record.year} • {t('common.meterReadingDay', 'Day')} {record.billingDay || 15}
                        </Typography>
                    </Box>
                </Grid>

                {/* Zone & Metering */}
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, height: '100%' }}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                            {t('common.meterUnits', 'Consumption & Zone')}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5, color: '#3b82f6' }}>
                            {formatKwh(record.totalKwh)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                            <Room sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                                {selectedZone && selectedZone !== 'all' ? selectedZone : t('common.wholeHouse', 'Whole House')}
                                {record.averagePowerWatt ? ` • ${formatWatt(record.averagePowerWatt)} avg` : ''}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                {/* Status Stamp */}
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: isPartial ? 'rgba(245, 158, 11, 0.08)' : 'rgba(16, 185, 129, 0.08)',
                            border: '1px dashed',
                            borderColor: isPartial ? 'warning.main' : 'success.main',
                            borderRadius: 2,
                            height: '100%',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                                {t('common.status', 'Status')}
                            </Typography>
                            <Chip
                                icon={isPartial ? <HourglassTop fontSize="small" /> : <CheckCircle fontSize="small" />}
                                label={isPartial ? t('common.cyclePartial', 'In Progress') : t('common.cycleCompleted', 'Completed')}
                                size="small"
                                color={isPartial ? 'warning' : 'success'}
                                sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                            />
                        </Box>

                        {isPartial ? (
                            <Box sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('common.cycleProgress', {
                                            percent: cycleProgress.progressPercent,
                                            elapsed: cycleProgress.elapsedDays,
                                            total: cycleProgress.totalDays,
                                        })}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                                        {cycleProgress.progressPercent}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={cycleProgress.progressPercent}
                                    color="warning"
                                    sx={{ height: 4, borderRadius: 2 }}
                                />
                            </Box>
                        ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {t('common.billingCycleFinalized', 'Cycle finalized on')} {formatShortDate(record.actualCalculationEnd || record.periodEnd)}
                            </Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>

            {/* Detailed Calculation Breakdown Table */}
            <TableContainer sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table size="small" aria-label="bill calculation table">
                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>{t('common.description', 'Item & Description')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{t('common.units', 'Units (kWh)')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{t('common.rate', 'Unit Rate (฿)')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{t('common.amount', 'Amount (฿)')}</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {/* Tier 1 */}
                        <TableRow hover>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {t('common.tier1Title', 'Tier 1: 1st – 150th Unit')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Base residential block 1 (1–150 kWh)
                                </Typography>
                            </TableCell>
                            <TableCell align="right">{record.tier1Units !== undefined ? record.tier1Units.toFixed(1) : '—'}</TableCell>
                            <TableCell align="right">3.2484</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(record.tier1Cost)}
                            </TableCell>
                        </TableRow>

                        {/* Tier 2 */}
                        <TableRow hover>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {t('common.tier2Title', 'Tier 2: 151st – 400th Unit')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Base residential block 2 (151–400 kWh)
                                </Typography>
                            </TableCell>
                            <TableCell align="right">{record.tier2Units !== undefined ? record.tier2Units.toFixed(1) : '—'}</TableCell>
                            <TableCell align="right">4.2218</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(record.tier2Cost)}
                            </TableCell>
                        </TableRow>

                        {/* Tier 3 */}
                        <TableRow hover>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {t('common.tier3Title', 'Tier 3: Above 400th Unit')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Base residential block 3 (> 400 kWh)
                                </Typography>
                            </TableCell>
                            <TableCell align="right">{record.tier3Units !== undefined ? record.tier3Units.toFixed(1) : '—'}</TableCell>
                            <TableCell align="right">4.4217</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(record.tier3Cost)}
                            </TableCell>
                        </TableRow>

                        {/* Base Energy Cost Subtotal */}
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                                {t('common.baseEnergyCost', 'Base Energy Cost Subtotal')}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatCurrency(record.baseEnergyCost)}
                            </TableCell>
                        </TableRow>

                        {/* Service Fee */}
                        <TableRow hover>
                            <TableCell colSpan={3}>
                                <Typography variant="body2">
                                    {t('common.serviceFee', 'Monthly Service Fee')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Standard low-voltage connection charge
                                </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(record.serviceFee)}
                            </TableCell>
                        </TableRow>

                        {/* Fuel Adjustment (Ft) */}
                        <TableRow hover>
                            <TableCell colSpan={2}>
                                <Typography variant="body2">
                                    {t('common.ftCost', 'Fuel Adjustment Charge (Ft)')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {formatKwh(record.totalKwh)} × {record.ftRate || 0.1623} ฿/unit
                                </Typography>
                            </TableCell>
                            <TableCell align="right">{record.ftRate || 0.1623}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(record.ftCost)}
                            </TableCell>
                        </TableRow>

                        {/* Subtotal Before VAT */}
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                                {t('common.totalBeforeVat', 'Subtotal Before VAT')}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatCurrency(record.totalBeforeVat)}
                            </TableCell>
                        </TableRow>

                        {/* VAT (7%) */}
                        <TableRow hover>
                            <TableCell colSpan={3}>
                                <Typography variant="body2">
                                    {t('common.vatRate', { rate: record.vatRate || DEFAULT_VAT_RATE })}
                                </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(record.vatAmount)}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Total Amount Due Box */}
            <Box
                sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 2,
                    bgcolor: 'rgba(16, 185, 129, 0.08)',
                    border: '2px solid #10b981',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 1 }}>
                        {t('common.totalCost', 'Net Total Amount Due')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                        {isPartial
                            ? t('common.inProgressNotice', 'Current cycle is in progress. Cost is estimated up to the latest sensor timestamp.')
                            : `${monthName} ${record.year} • ${t('common.calculatedFromTiers', 'All regulatory charges & taxes included')}`}
                    </Typography>
                </Box>

                <Typography variant="h3" sx={{ fontWeight: 900, color: '#10b981' }}>
                    {formatCurrency(record.totalCost)}
                </Typography>
            </Box>
        </Paper>
    );
}

export default DigitalBillReceipt;
